import { useCallback, type SyntheticEvent } from 'react';
import type { BlockComponentProps } from '@unifyapps/carbon/no-code/components/BlockRenderer/types';
import type { CardComponentType, CardBlockStateType } from '@unifyapps/defs/types/blocks';
import useBlockEvents from '@unifyapps/carbon/no-code/hooks/useBlockEvents';
import _set from 'lodash/set';
import { isEmptyBlock } from '@unifyapps/carbon/no-code/utils/block';
import { EventTypeEnum } from '@unifyapps/defs/types/event';
import { memo } from 'react';
import useForkRef from '@unifyapps/ui/hooks/useForkRef';
import { CardLayout, CardLayoutSlots } from './CardLayout';
import { useCollapsibleParams } from './hooks/useCollapsibleParams';

function Card({
  blockState,
  attributesAndEventListeners,
  events,
  component,
  block,
  updateBlockState,
  blockRef: _blockRef,
  dndRef,
  computedBlockState,
  registry,
}: BlockComponentProps<CardComponentType, CardBlockStateType, CardBlockStateType>) {
  const blockRef = useForkRef(_blockRef, dndRef);

  const BlockRenderer = registry.getBlockRenderer();

  const { appearance } = computedBlockState;
  const { slots } = component;

  const { emitEvent } = useBlockEvents(events);

  const hasClickEvent = events?.some((event) => event.eventType === 'onClick');

  const onClick = useCallback(
    (e: SyntheticEvent) => {
      if (hasClickEvent) {
        void emitEvent({ eventType: EventTypeEnum.OnClick, domEvent: e });
      }
    },
    [emitEvent, hasClickEvent],
  );

  const { collapsibleParams } = useCollapsibleParams({
    blockState,
    updateBlockState,
  });

  return (
    <CardLayout
      appearance={appearance}
      attributesAndEventListeners={attributesAndEventListeners}
      block={block}
      blockRef={blockRef}
      collapsibleParams={collapsibleParams}
      hasClickEvent={hasClickEvent}
      onClick={onClick}
    >
      <CardLayout.Slot name={CardLayoutSlots.Header}>
        {slots?.header?.blockId && !isEmptyBlock(slots.header.blockId) ? (
          <BlockRenderer
            blockId={slots.header.blockId}
            key={slots.header.blockId}
            parentBlockId={blockState.id}
          />
        ) : null}
      </CardLayout.Slot>
      <CardLayout.Slot name={CardLayoutSlots.Body}>
        {slots?.body?.blockId && !isEmptyBlock(slots.body.blockId) ? (
          <BlockRenderer blockId={slots.body.blockId} parentBlockId={blockState.id} />
        ) : null}
      </CardLayout.Slot>
      <CardLayout.Slot name={CardLayoutSlots.Footer}>
        {slots?.footer?.blockId && !isEmptyBlock(slots.footer.blockId) ? (
          <BlockRenderer blockId={slots.footer.blockId} parentBlockId={blockState.id} />
        ) : null}
      </CardLayout.Slot>
    </CardLayout>
  );
}

export default memo(Card);
